import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { fetchAllLeaderboardData, getActivePeriod, setActivePeriod, getActiveGameMode, setActiveGameMode } from '../utils/leaderboardApi';

/**
 * LeaderboardSection Component
 * Displays leaderboards with tab navigation for different time periods:
 * - Weekly: Current week's top 10
 * - Monthly: Current month's top 10
 * - YTD: Year-to-date top 10
 * - All-Time: All-time top 10
 *
 * Uses single API call per period - game mode filtering is done locally (no additional API calls)
 */
const LeaderboardSection = () => {
  const { t } = useLanguage();
  const [activePeriod, setActivePeriodState] = useState(getActivePeriod());
  const [activeGameModeState, setActiveGameModeState] = useState(getActiveGameMode());
  const [leaderboardData, setLeaderboardData] = useState(null); // Contains all game modes
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use refs instead of state to prevent infinite loops in useEffect
  const isRefreshingRef = useRef(false);
  const lastRefreshTimeRef = useRef(0);

  // Periods configuration
  const periods = [
    { id: 'weekly', label: t('leaderboard.weekly'), shortLabel: t('leaderboard.weeklyShort') || 'Week' },
    { id: 'monthly', label: t('leaderboard.monthly'), shortLabel: t('leaderboard.monthlyShort') || 'Month' },
    { id: 'ytd', label: t('leaderboard.ytd'), shortLabel: t('leaderboard.ytdShort') || 'Year' },
    { id: 'alltime', label: t('leaderboard.allTime'), shortLabel: t('leaderboard.allTimeShort') || 'All' }
  ];

  // Game mode options
  const gameModes = [
    { id: 'all', label: t('leaderboard.allModes') || 'All' },
    { id: 'regular', label: t('leaderboard.regular') || 'Regular' },
    { id: '15s', label: '15s' },
    { id: '30s', label: '30s' },
    { id: '45s', label: '45s' },
    { id: '60s', label: '60s' }
  ];

  // Fetch ALL leaderboard data for a period (includes all game modes in one call)
  const loadLeaderboard = useCallback(async (period, bypassCooldown = false) => {
    // Debounce: Prevent rapid successive calls (3-second cooldown)
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTimeRef.current;

    if (!bypassCooldown && timeSinceLastRefresh < 3000 && lastRefreshTimeRef.current > 0) {
      console.log(`Leaderboard refresh cooldown active. Please wait ${Math.ceil((3000 - timeSinceLastRefresh) / 1000)}s`);
      return;
    }

    if (isRefreshingRef.current) {
      console.log('Leaderboard refresh already in progress');
      return;
    }

    try {
      isRefreshingRef.current = true;
      setLoading(true);
      setError(null);
      lastRefreshTimeRef.current = now;

      // Fetch ALL game modes in one call
      const data = await fetchAllLeaderboardData(period);
      setLeaderboardData(data);
    } catch (err) {
      console.error(`Failed to load ${period} leaderboard:`, err);
      setError(err.message);
    } finally {
      setLoading(false);
      // Keep isRefreshing true for a short period to prevent double-clicks
      setTimeout(() => {
        isRefreshingRef.current = false;
      }, 1000);
    }
  }, []);

  // Handle tab change - fetches new data
  const handlePeriodChange = (period) => {
    setActivePeriodState(period);
    setActivePeriod(period); // Save to localStorage
    loadLeaderboard(period);
  };

  // Handle game mode change - NO API call, just filter locally!
  const handleGameModeChange = (gameMode) => {
    setActiveGameModeState(gameMode);
    setActiveGameMode(gameMode); // Save to localStorage
    // No loadLeaderboard call - data is already loaded, just re-render with new filter
  };

  // Load on mount and when period changes (NOT when gameMode changes - that's local filtering)
  useEffect(() => {
    loadLeaderboard(activePeriod, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePeriod]); // Only depend on period, not gameMode

  // Auto-refresh every 15 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      loadLeaderboard(activePeriod, true);
    }, 900000); // Refresh every 15 minutes (matches server cache TTL for optimal performance)

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePeriod]);

  // Get medal emoji for top 3
  const getMedal = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `${rank}.`;
  };

  // Get period title
  const getPeriodTitle = () => {
    switch (activePeriod) {
      case 'weekly':
        return t('leaderboard.thisWeek');
      case 'monthly':
        return t('leaderboard.thisMonth');
      case 'ytd':
        return t('leaderboard.thisYear');
      case 'alltime':
        return t('leaderboard.allTimeRankings');
      default:
        return t('leaderboard.title');
    }
  };

  // Get leaderboard data for current game mode (LOCAL filtering - no API call)
  const getLeaderboardForMode = () => {
    if (!leaderboardData || !leaderboardData.byGameMode) return { topPlayers: [], totalEntries: 0 };
    return leaderboardData.byGameMode[activeGameModeState] || { topPlayers: [], totalEntries: 0 };
  };

  const currentModeData = getLeaderboardForMode();
  const players = currentModeData.topPlayers || [];
  const totalEntries = currentModeData.totalEntries || 0;

  return (
    <div className="leaderboard-section">
      {/* Header */}
      <h2 className="leaderboard-title">
        {t('leaderboard.title')}
      </h2>

      {/* Tab Navigation */}
      <div className="leaderboard-tabs">
        {periods.map((period) => (
          <button
            key={period.id}
            className={`leaderboard-tab ${activePeriod === period.id ? 'active' : ''}`}
            onClick={() => handlePeriodChange(period.id)}
            aria-label={period.label}
          >
            <span className="tab-label-full">{period.label}</span>
            <span className="tab-label-short">{period.shortLabel}</span>
          </button>
        ))}
      </div>

      {/* Game Mode Filter */}
      <div className="leaderboard-filter" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <label htmlFor="gameMode-filter" style={{ fontSize: '0.875rem', color: '#666' }}>
          {t('leaderboard.gameMode') || 'Mode'}:
        </label>
        <select
          id="gameMode-filter"
          value={activeGameModeState}
          onChange={(e) => handleGameModeChange(e.target.value)}
          style={{
            padding: '0.375rem 0.75rem',
            borderRadius: '0.375rem',
            border: '1px solid #ddd',
            fontSize: '0.875rem',
            background: 'white',
            cursor: 'pointer'
          }}
        >
          {gameModes.map((mode) => (
            <option key={mode.id} value={mode.id}>
              {mode.label}
            </option>
          ))}
        </select>
      </div>

      {/* Period subtitle */}
      <p className="leaderboard-subtitle">
        {getPeriodTitle()}
      </p>

      {/* Loading state */}
      {loading && (
        <div className="leaderboard-loading">
          {t('leaderboard.loading')}
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="leaderboard-error">
          {t('leaderboard.error')}
          <button
            onClick={() => loadLeaderboard(activePeriod)}
            style={{
              marginTop: '0.5rem',
              padding: '0.5rem 1rem',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && players.length === 0 && (
        <div className="leaderboard-empty">
          {t('leaderboard.noScoresYet') || t('leaderboard.noDataYet')}
        </div>
      )}

      {/* Leaderboard table */}
      {!loading && !error && players.length > 0 && (
        <div className="leaderboard-table-container">
          <table className="leaderboard-table">
            <thead>
              <tr>
                <th className="leaderboard-th rank-col">
                  {t('leaderboard.rank')}
                </th>
                <th className="leaderboard-th player-col">
                  {t('leaderboard.player')}
                </th>
                <th className="leaderboard-th score-col">
                  {t('leaderboard.score')}
                </th>
              </tr>
            </thead>
            <tbody>
              {players.map((player) => {
                const rank = player.rank;
                const isTopThree = rank <= 3;

                return (
                  <tr
                    key={`${player.gameId}-${player.timestamp}-${rank}`}
                    className={`leaderboard-row ${isTopThree ? 'top-three' : ''}`}
                  >
                    <td className="leaderboard-td rank-col">
                      <span className={isTopThree ? 'medal' : 'rank-number'}>
                        {getMedal(rank)}
                      </span>
                    </td>
                    <td className="leaderboard-td player-col">
                      {player.playerName}
                    </td>
                    <td className="leaderboard-td score-col">
                      {player.score}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer - Total entries */}
      {!loading && !error && totalEntries > 0 && (
        <div className="leaderboard-footer">
          {t('leaderboard.totalEntries').replace('{count}', totalEntries)}
        </div>
      )}
    </div>
  );
};

export default LeaderboardSection;
